import type { Theme } from '@mui/material/styles';

import { LanguageKeys } from 'src/common/constants/languages';
import { FRONTEND_FLOAT_IMAGE_MOBILE_MAX, parseImageVisualFromClass } from 'src/helpers/tinymceImageStyles';
import {
  IImageBlock,
  IImageBlockDisplayOptions,
  IPageBlockImageItem,
} from 'src/interfaces/PageBlock';

interface ImageDisplayOptionsInput {
  alignment?: string;
  size?: string;
  style?: string;
  width?: string;
  height?: string;
}

/** Build TinyMCE-compatible class string from display options. */
export function imageDisplayOptionsToClassName(
  display?: ImageDisplayOptionsInput | IImageBlockDisplayOptions | null,
): string {
  const classes = ['img-responsive'];
  if (display?.alignment) classes.push(display.alignment);
  if (display?.size) classes.push(display.size);
  if (display?.style) classes.push(display.style);
  return classes.join(' ');
}

/** Map stored width/height to MUI sx (matches parseTinyMCEContent explicit attrs). */
export function imageDisplayOptionsToExplicitSx(
  display?: ImageDisplayOptionsInput | IImageBlockDisplayOptions | null,
): Record<string, unknown> {
  const sx: Record<string, unknown> = {};
  if (isMeaningfulCssDimension(display?.width)) sx.width = display!.width!.trim();
  if (isMeaningfulCssDimension(display?.height)) sx.height = display!.height!.trim();
  return sx;
}

const STACK_SIZE_WIDTH: Record<string, string> = {
  'img-small': '200px',
  'img-medium': '400px',
  'img-large': '600px',
  'img-full': '100%',
};

const DEFAULT_STACK_WIDTH = '220px';
const STACK_ASPECT_PADDING = '133.333%'; // height = width * 4/3 (portrait 3:4)

function isMeaningfulCssDimension(value?: string): boolean {
  if (!value?.trim()) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== 'auto' && normalized !== 'inherit' && normalized !== 'initial';
}

interface StackedGalleryLayout {
  width: string;
  paddingBottom?: string;
  height?: string;
  float?: 'left' | 'right';
  margin?: string;
  display?: 'block';
  maxWidth?: string | Record<string, string>;
  borderRadius?: string | number;
  overflow?: 'hidden';
  boxShadow?: string;
}

/** Resolve width/height/float for the gallery stack outer shell. */
function resolveStackedGalleryLayout(
  display?: IImageBlockDisplayOptions | null,
): StackedGalleryLayout {
  const alignment = display?.alignment;
  const size = display?.size;
  const isFloated = alignment === 'img-left' || alignment === 'img-right';

  let width = DEFAULT_STACK_WIDTH;
  if (isMeaningfulCssDimension(display?.width)) {
    width = display!.width!.trim();
  } else if (size && STACK_SIZE_WIDTH[size]) {
    width = STACK_SIZE_WIDTH[size];
  }

  const layout: StackedGalleryLayout = {
    width,
    maxWidth: '100%',
  };

  if (isMeaningfulCssDimension(display?.height)) {
    layout.height = display!.height!.trim();
  } else {
    layout.paddingBottom = STACK_ASPECT_PADDING;
  }

  if (isFloated) {
    layout.float = alignment === 'img-left' ? 'left' : 'right';
    layout.margin = alignment === 'img-left' ? '0 1rem 1rem 0' : '0 0 1rem 1rem';
    layout.maxWidth = {
      xs: FRONTEND_FLOAT_IMAGE_MOBILE_MAX,
      sm: width,
    };
  } else if (alignment === 'img-center') {
    layout.display = 'block';
    layout.margin = '0 auto 1rem auto';
  }

  return layout;
}

/**
 * Layout sx for gallery stack containers. Unlike parseImageVisualFromClass (for img tags),
 * this keeps explicit dimensions so absolutely-positioned stack layers have a sized box.
 */
export function parseStackedGalleryContainerSx(
  display?: IImageBlockDisplayOptions | null,
): () => Record<string, unknown> {
  return () => {
    const layout = resolveStackedGalleryLayout(display);
    const sx: Record<string, unknown> = {
      position: 'relative',
      display: 'block',
      width: layout.width,
      maxWidth: layout.maxWidth,
      boxSizing: 'border-box',
    };

    if (layout.height) {
      sx.height = layout.height;
    } else {
      sx.paddingBottom = layout.paddingBottom;
    }

    if (layout.float) sx.float = layout.float;
    if (layout.margin) sx.margin = layout.margin;

    return sx;
  };
}

/**
 * Per-image sx for gallery stack layers — same class tokens as RichTextImage / single image blocks,
 * adapted to fill the stack frame (no float/margin/auto sizing on each layer).
 */
export function parseImageBlockLayerSx(
  display?: IImageBlockDisplayOptions | null,
): (theme: Theme) => Record<string, unknown> {
  const className = imageDisplayOptionsToClassName(display);

  return (theme: Theme) => {
    const parsed = parseImageVisualFromClass(className);
    const fromClass =
      typeof parsed === 'function'
        ? (parsed(theme) as Record<string, unknown>)
        : ((parsed ?? {}) as Record<string, unknown>);

    return {
      ...fromClass,
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      float: undefined,
      margin: undefined,
      display: undefined,
    };
  };
}

export function resolveImageBlockAlt(
  alt: Record<LanguageKeys, string> | undefined,
  lang: LanguageKeys,
): string {
  return alt?.[lang]?.trim() || alt?.en?.trim() || '';
}

export function resolveGalleryItemAlt(
  item: IPageBlockImageItem,
  lang: LanguageKeys,
): string {
  return resolveImageBlockAlt(item.alt, lang);
}

export function imageBlockToPageImages(
  block: IImageBlock,
  lang: LanguageKeys,
): { src: string; alt: string }[] {
  if (block.mode === 'gallery') {
    return (block.images ?? [])
      .filter(item => item.imageUrl?.trim())
      .map(item => ({
        src: item.imageUrl,
        alt: resolveGalleryItemAlt(item, lang),
      }));
  }

  const src = block.imageUrl?.trim();
  if (!src) return [];
  return [{ src, alt: resolveImageBlockAlt(block.alt, lang) }];
}
